import { lazy, Suspense, useCallback } from 'react'
import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router'

import {
  DEFAULT_EMAIL_SECTION,
  EmailSidebar,
  isEmailSection,
  MEMBER_EMAIL_SECTION,
  type EmailSection,
} from '@/components/settings/email-sidebar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { requirePermission, PermissionDeniedRoute } from '@/lib/require-permission-guard'

const SendersSection = lazy(() =>
  import('@/components/settings/email/senders-section').then((module) => ({
    default: module.SendersSection,
  }))
)
const TemplatesSection = lazy(() =>
  import('@/components/settings/email/templates-section').then((module) => ({
    default: module.TemplatesSection,
  }))
)
const SystemTemplatesSection = lazy(() =>
  import('@/components/settings/email/system-templates-section').then((module) => ({
    default: module.SystemTemplatesSection,
  }))
)
const BlockedRecipientsSection = lazy(() =>
  import('@/components/settings/email/email-inbox-sections').then((module) => ({
    default: module.BlockedRecipientsSection,
  }))
)
const ConnectedInboxSection = lazy(() =>
  import('@/components/settings/email/connected-inbox-section').then((module) => ({
    default: module.ConnectedInboxSection,
  }))
)
const NeverCaptureSection = lazy(() =>
  import('@/components/settings/email/never-capture-section').then((module) => ({
    default: module.NeverCaptureSection,
  }))
)
const CompanyDomainsSection = lazy(() =>
  import('@/components/settings/email/company-domains-section').then((module) => ({
    default: module.CompanyDomainsSection,
  }))
)

const MyNotificationsSection = lazy(() =>
  import('@/components/settings/email/my-notifications-section').then((module) => ({
    default: module.MyNotificationsSection,
  }))
)

const SenderSuggestionsSetting = lazy(() =>
  import('@/components/settings/email/sender-suggestions-setting').then((module) => ({
    default: module.SenderSuggestionsSetting,
  }))
)

type EmailSearch = {
  section?: EmailSection
  // Set by the Nylas OAuth callback redirect (handler_nylas_oauth.go): code
  // and state on success, connect=error and a reason otherwise.
  connect?: 'error'
  reason?: string
  code?: string
  state?: string
}

// Legacy `?section=` keys kept working for bookmarks and older links. Keys
// for removed sections (custom mail domain, deliverability, inbox issues)
// land on the closest surviving section. A legacy `healthTab` param is
// dropped from the resulting URL because each health view is its own section.
const legacySectionMap: Record<string, EmailSection> = {
  overview: 'senders',
  'connect-gmail': 'senders',
  domain: 'senders',
  'domain-inbox': 'senders',
  'email-domain': 'senders',
  'email-inbox': 'senders',
  deliverability: 'blocked-recipients',
  'inbox-issues': 'senders',
  templates: 'saved-templates',
  'system-templates': 'automated-emails',
  'health-safety': 'blocked-recipients',
}

export const Route = createFileRoute('/_app/_settings/email')({
  component: EmailPage,
  validateSearch: (search: Record<string, unknown>): EmailSearch => {
    const raw = search.section
    // Unknown values fall back to the default instead of rendering nothing.
    const section =
      typeof raw === 'string' && (isEmailSection(raw) || legacySectionMap[raw])
        ? raw
        : DEFAULT_EMAIL_SECTION
    // A legacy key survives validation only long enough for `beforeLoad` to
    // redirect it, so it is typed as the section it resolves to.
    return {
      section: section as EmailSection,
      connect: search.connect === 'error' ? 'error' : undefined,
      reason: typeof search.reason === 'string' ? search.reason : undefined,
      code: typeof search.code === 'string' ? search.code : undefined,
      state: typeof search.state === 'string' ? search.state : undefined,
    }
  },
  // Templates are Comms; senders and blocked recipients are Settings —
  // either one gets you in. My notifications is every member's own opt-out
  // (the digest email footer links there), so it needs neither.
  beforeLoad: async ({ context, search }) => {
    const rawSection = search.section as string | undefined
    if (rawSection && !isEmailSection(rawSection) && legacySectionMap[rawSection]) {
      throw redirect({ to: '/email', search: { section: legacySectionMap[rawSection] } })
    }
    if (rawSection === MEMBER_EMAIL_SECTION) return
    await requirePermission(context.queryClient, ['CommsView', 'SettingsManage'], 'email settings')
  },
  errorComponent: PermissionDeniedRoute,
})

function EmailPage() {
  useBreadcrumbFromRoute()
  const search = useSearch({ from: '/_app/_settings/email' })
  const currentSection: EmailSection = isEmailSection(search.section)
    ? search.section
    : DEFAULT_EMAIL_SECTION
  const { hasFeature: hasEmailInbox } = useHasEmailInboxFeature()
  const navigate = useNavigate()
  // The hosted-auth code is single use and shouldn't sit in history.
  const clearConnectCallback = useCallback(() => {
    void navigate({ to: '/email', search: { section: 'connected-inbox' }, replace: true })
  }, [navigate])

  return (
    <div className="flex h-full overflow-hidden">
      <div className="scroll-fade-y flex-shrink-0 overflow-y-auto">
        <EmailSidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          <Suspense fallback={<SectionLoadingSkeleton />}>
            {currentSection === 'senders' && <SendersSection />}
            {currentSection === 'saved-templates' && <TemplatesSection />}
            {currentSection === 'automated-emails' && <SystemTemplatesSection />}
            {currentSection === 'blocked-recipients' && <BlockedRecipientsSection />}
            {currentSection === 'connected-inbox' && hasEmailInbox && (
              <div>
                <ConnectedInboxSection
                  connect={search.connect}
                  reason={search.reason}
                  code={search.code}
                  state={search.state}
                  onCallbackHandled={clearConnectCallback}
                />
                <SenderSuggestionsSetting />
                <NeverCaptureSection />
                <CompanyDomainsSection />
              </div>
            )}
            {currentSection === 'my-notifications' && <MyNotificationsSection />}
          </Suspense>
        </div>
      </div>
    </div>
  )
}

function SectionLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div>
        <Skeleton className="mb-2 h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
